import { Children, FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";

interface AccessReportsWizardStepProps {
  children?: React.ReactNode;
  currentWizardStep: number;
  nextPath?: string;
  nextPathDisabled?: boolean;
  nextPathDisabledTooltipText?: string;
  onSubmitClick?: () => void;
  previousPath?: string;
  setWizardStep: (page: number) => void;
  wizardSteps: string[];
}

export const AccessReportsWizardStep: FC<AccessReportsWizardStepProps> = (props: AccessReportsWizardStepProps) => {
  const wizardBodyRef = useRef<HTMLDivElement>(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, [props.currentWizardStep]);

  const onSubmitClick = (): void => {
    setSubmitting(true);
    props.onSubmitClick();
  }

  const hasChildren = Children.count(props.children) > 0;

  return (
    <Page className="bg-white" documentTitle="New Access Report">
      <PageInner>
        <PageSection>
          <div className="row">
            <div className="col-12">
              <h2 className="mt-3 mb-3 header-l demibold">
                New Access Report
              </h2>
            </div>
          </div>

          <div className="row">
            <div className="col-11">
              <WizardSteps
                currentStep={props.currentWizardStep}
                setStep={props.setWizardStep}
                steps={props.wizardSteps}
              />
            </div>

            <div className="col-12">
              <hr />
            </div>
          </div>

          <div ref={wizardBodyRef}>
            {hasChildren && props.children}
          </div>

          <div className="row">
            <div className="col-12">
              <hr />
            </div>
          </div>

        </PageSection>
      </PageInner>
      <WizardNavigationBar
        leftSide={
          <Link
            className="qes-button qes-button-secondary danger"
            id="access-reports-wizard__step--cancel"
            to={{
              pathname: `/access-reports`,
            }}
          >
            <b>Cancel</b>
          </Link>
        }
        rightSide={
          <div>
            {props.previousPath &&
              <Link
                className="text-info"
                id="access-reports-wizard__step--previous"
                to={{
                  pathname: `${props.previousPath}`,
                }}
              >
                <b>Previous</b>
              </Link>
            }

            {props.nextPath &&
              (props.nextPathDisabled ?
                <TooltipWrapper
                  containerClassName="ms-5"
                  text={props.nextPathDisabledTooltipText}
                  position="top"
                >
                  <div
                    id="access-reports-wizard__step--disabled-next"
                    className="btn qes-button qes-button-primary disabled pe-5 ps-5"
                  >
                    Next
                  </div>
                </TooltipWrapper> :
                <Link
                  id="access-reports-wizard__step--next"
                  className="btn qes-button qes-button-primary ms-5 pe-5 ps-5"
                  onClick={(e) => e.currentTarget.blur()}
                  to={{
                    pathname: `${props.nextPath}`,
                  }}
                >
                  Next
                </Link>)
            }

            {props.onSubmitClick && (
              <QESButton
                className="btn cursor-pointer ms-5 pe-5 ps-5"
                disabled={submitting}
                id="access-reports-wizard__step--submit"
                onClick={onSubmitClick}
                qesTestId="access_reports_wizard_step_submit"
                qesType="primary"
              >
                Submit
              </QESButton>
            )}
          </div>
        }
      />
    </Page>
  );
}
